const host = () => process.env.REACT_APP_HOST_SERVER || '';

export const FEEDBACK_CATEGORIES = [
  { id: 'suggestion', label: 'Suggestion', hint: 'Share an idea' },
  { id: 'bug', label: 'Bug', hint: 'Something broke' },
  { id: 'question', label: 'Question', hint: 'Need a hand' },
  { id: 'compliment', label: 'Compliment', hint: 'What you love' },
  { id: 'other', label: 'Other', hint: 'Anything else' },
];

const SUBJECT_MAX = 120;
const MESSAGE_MAX = 2000;

function authHeaders(token) {
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function parseJson(response) {
  return response.json().catch(() => ({}));
}

function unwrapList(json) {
  if (Array.isArray(json)) return json;
  const data = json?.data ?? json;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.feedback)) return data.feedback;
  if (Array.isArray(json?.items)) return json.items;
  return [];
}

function unwrapPagination(json, fallback) {
  return json?.data?.pagination || json?.pagination || fallback;
}

export function getCategoryLabel(value) {
  const match = FEEDBACK_CATEGORIES.find(
    (item) => item.id === String(value || '').toLowerCase()
  );
  if (match) return match.label;
  if (!value) return 'Other';
  return String(value).replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

export function getCategoryClass(value) {
  const id = String(value || 'other').toLowerCase();
  return FEEDBACK_CATEGORIES.some((item) => item.id === id) ? id : 'other';
}

export function validateFeedback({ category, subject, message }) {
  const errors = [];
  if (!FEEDBACK_CATEGORIES.some((item) => item.id === category)) {
    errors.push('Please choose a category.');
  }
  const trimmedSubject = String(subject || '').trim();
  const trimmedMessage = String(message || '').trim();
  if (!trimmedSubject) errors.push('Please add a subject.');
  if (trimmedSubject.length > SUBJECT_MAX) {
    errors.push(`Subject must be ${SUBJECT_MAX} characters or fewer.`);
  }
  if (!trimmedMessage) errors.push('Please describe your feedback.');
  if (trimmedMessage.length > MESSAGE_MAX) {
    errors.push(`Feedback must be ${MESSAGE_MAX} characters or fewer.`);
  }
  return {
    ok: errors.length === 0,
    errors,
    values: {
      category,
      subject: trimmedSubject,
      message: trimmedMessage,
    },
  };
}

export async function submitFeedback(payload, token) {
  const response = await fetch(`${host()}/api/feedback`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  const json = await parseJson(response);

  if (response.status === 401) {
    throw new Error('Please sign in to submit feedback.');
  }
  if (!response.ok || json.successful === false) {
    throw new Error(json.message || 'Unable to submit feedback. Please try again.');
  }

  return {
    message: json.message || 'Thanks — we received your feedback.',
    item: json.data || json.feedback || null,
  };
}

export async function fetchMyFeedback(token) {
  const response = await fetch(`${host()}/api/feedback`, {
    method: 'GET',
    headers: authHeaders(token),
  });
  const json = await parseJson(response);
  if (!response.ok || json.successful === false) {
    return { items: [], maxPerUser: null, remaining: null };
  }
  const data = json?.data ?? json;
  const items = unwrapList(json);
  const maxPerUser = Number.isFinite(Number(data?.maxPerUser)) ? Number(data.maxPerUser) : null;
  const remaining = Number.isFinite(Number(data?.remaining))
    ? Number(data.remaining)
    : maxPerUser == null
      ? null
      : Math.max(maxPerUser - items.length, 0);
  return { items, maxPerUser, remaining };
}

export async function fetchAdminFeedback({ page = 1, limit = 10, search = '', token }) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (search) params.set('search', search);

  const response = await fetch(`${host()}/api/admin/feedback?${params.toString()}`, {
    method: 'GET',
    headers: authHeaders(token),
  });
  const json = await parseJson(response);

  if (response.status === 403) {
    throw new Error('You do not have admin access.');
  }
  if (!response.ok || json.successful === false) {
    throw new Error(json.message || 'Unable to load feedback.');
  }

  const items = unwrapList(json);
  const pagination = unwrapPagination(json, { page, limit, total: items.length });
  return { items, pagination };
}

export { SUBJECT_MAX, MESSAGE_MAX };
